import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStoreState } from '~/store';
import { api } from '~/api';
import { Order, Trade } from '~/models';
import { useMemo } from 'react';
import { useFilters } from '~/components/filters/context';
import filterOrders from './filterMarket';
import { useProducts } from '~/hooks/useProducts';

/**
 * Hook to invalidate market orders cache, triggering a refetch.
 * Use this after mutations that affect market orders (trades, counters, deletions, etc.)
 */
export const useInvalidateMarketOrders = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['marketOrders'] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  };
};

interface MarketOrdersParams {
  solution: string;
  warehouse?: string;
}

interface MarketOrdersData {
  orders: Order[];
  updatedDate: Date | null;
}

/**
 * Base hook for fetching market orders with caching and if-modified-since support.
 * Returns raw orders data without any filtering or sorting.
 * Used by Navbar (for counts), useFilterOptions (for origins), and Market page (via useFilteredMarketOrders).
 */
export const useMarketOrders = () => {
  const solution = useStoreState((state) => state.auth.solution);
  const { warehouse } = useFilters();
  const queryClient = useQueryClient();

  const queryKey = ['marketOrders', solution?.id, warehouse?.id];

  return useQuery<MarketOrdersData>({
    queryKey,
    queryFn: async () => {
      if (!solution?.id) {
        return { orders: [], updatedDate: null };
      }

      const params: MarketOrdersParams = { solution: solution.id };
      if (warehouse) {
        params.warehouse = warehouse.id;
      }

      const previousData = queryClient.getQueryData<MarketOrdersData>(queryKey);
      const updatedDate = previousData?.updatedDate;

      try {
        const result = await api.getDataIfModifiedSince(
          '/market-orders',
          params,
          updatedDate
        );

        if (!result) {
          return previousData || { orders: [], updatedDate: null };
        }

        const [status, response] = result;

        if (status === 200 && response) {
          const changed = response.orders.map((o: any) => new Order(o));
          const workingOrders: Order[] = [];
          const changedIds: string[] = [];

          changed.forEach((o: Order) => {
            if (o.status === 'Working') {
              workingOrders.push(o);
            }
            changedIds.push(o.id);
          });

          // Merge with existing orders if this is an update
          if (updatedDate && previousData?.orders) {
            previousData.orders.forEach((order) => {
              if (!changedIds.includes(order.id)) {
                workingOrders.push(order);
              }
            });
          }

          return {
            orders: workingOrders,
            updatedDate: new Date(),
          };
        }

        // 304 Not Modified - return previous data with updated timestamp
        if (status === 304) {
          return previousData || { orders: [], updatedDate: new Date() };
        }

        // Unexpected status
        return previousData || { orders: [], updatedDate: null };
      } catch (error) {
        console.error('Error fetching market orders:', error);
        return previousData || { orders: [], updatedDate: null };
      }
    },
    enabled: !!solution?.id,
    refetchInterval: 30000, // 30 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is in background
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Only refetch on reconnect
    refetchOnMount: false, // Don't refetch when component remounts
    staleTime: 30000,
  });
};

interface MarketTradesData {
  trades: Trade[];
  updatedDate: Date | null;
}

/**
 * Hook for fetching market trades with caching and if-modified-since support.
 * Used by Marketplace table for displaying trade data.
 */
export const useMarketTrades = () => {
  const solution = useStoreState((state) => state.auth.solution);
  const queryClient = useQueryClient();

  const queryKey = ['marketTrades', solution?.id];

  return useQuery<MarketTradesData>({
    queryKey,
    queryFn: async () => {
      if (!solution?.id) {
        return { trades: [], updatedDate: null };
      }

      const previousData = queryClient.getQueryData<MarketTradesData>(queryKey);
      const updatedDate = previousData?.updatedDate;

      try {
        const result = await api.getDataIfModifiedSince(
          '/market-trades',
          { solution: solution.id },
          updatedDate
        );

        if (!result) {
          return previousData || { trades: [], updatedDate: null };
        }

        const [status, response] = result;

        if (status === 200 && response) {
          const changed = response.trades.map((t: any) => new Trade(t));
          let trades: Trade[] = [];

          // Merge with existing trades if this is an update
          if (updatedDate && previousData?.trades) {
            const changedIds = changed.map((t: Trade) => t.id);
            previousData.trades.forEach((trade) => {
              const idx = changedIds.indexOf(trade.id);
              if (idx === -1) {
                trades.push(trade);
              } else {
                trades.push(changed[idx]);
              }
            });
            // Add any new trades not in previous data
            changed.forEach((t: Trade) => {
              if (!trades.find((existing) => existing.id === t.id)) {
                trades.push(t);
              }
            });
          } else {
            trades = changed;
          }

          return {
            trades,
            updatedDate: new Date(),
          };
        }

        // 304 Not Modified - return previous data
        if (status === 304) {
          return previousData || { trades: [], updatedDate: new Date() };
        }

        return previousData || { trades: [], updatedDate: null };
      } catch (error) {
        console.error('Error fetching market trades:', error);
        return previousData || { trades: [], updatedDate: null };
      }
    },
    enabled: !!solution?.id,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: false,
    staleTime: 30000,
  });
};

type SortableValue = string | number | Date | null | undefined;

function getValue(obj: any, path: string[]): SortableValue {
  let value = obj;
  for (const key of path) {
    value = value?.[key];
    if (value === undefined || value === null) return null;
  }
  return value;
}

function compareValues(
  a: SortableValue,
  b: SortableValue,
  ascending: boolean
): number {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  if (a < b) return ascending ? -1 : 1;
  if (a > b) return ascending ? 1 : -1;
  return 0;
}

function sortData(
  data: any[],
  sortFields: string[][],
  ascending: boolean
): any[] {
  return [...data].sort((a, b) => {
    for (const field of sortFields) {
      const aVal = getValue(a, field);
      const bVal = getValue(b, field);
      const comparison = compareValues(aVal, bVal, ascending);
      if (comparison !== 0) return comparison;
    }
    return 0;
  });
}

function getSorter(sortDirection: string, sortColumn: string) {
  const ascending = sortDirection === 'up';

  switch (sortColumn) {
    case 'etd-from':
      return (data: any[]) =>
        sortData(data, [['etd', 'from'], ['updated']], ascending);
    case 'etd-to':
      return (data: any[]) =>
        sortData(data, [['etd', 'to'], ['updated']], ascending);
    case 'origins':
      return (data: any[]) =>
        sortData(data, [['origins', '0', 'name'], ['updated']], ascending);
    case 'locations':
      return (data: any[]) =>
        sortData(data, [['locations', '0', 'name'], ['updated']], ascending);
    case 'product':
      return (data: any[]) =>
        sortData(data, [['product', 'name'], ['updated']], ascending);
    case 'created':
      return (data: any[]) => sortData(data, [['created']], ascending);
    case 'ref-price':
      return (data: any[]) =>
        sortData(
          data,
          [['price', 'std', 'unit'], ['price', 'std', 'price'], ['updated']],
          ascending
        );
    case 'price':
      return (data: any[]) =>
        sortData(
          data,
          [['price', 'unit'], ['price', 'val'], ['updated']],
          ascending
        );
    case 'state':
      return (data: any[]) =>
        sortData(data, [['state'], ['updated']], ascending);
    default:
      return (data: any[]) => data;
  }
}

/**
 * Hook for the Market page that adds filtering and sorting to raw market orders.
 * Only this hook depends on filters and sorting state - keeps other consumers lightweight.
 * Used by the Market page Body component.
 */
export const useFilteredMarketOrders = (
  sortDirection: string,
  sortColumn: string
) => {
  const solution = useStoreState((state) => state.auth.solution);
  const hasRole = useStoreState((state) => state.auth.hasRole);
  const { filters } = useFilters();

  const { data: products } = useProducts(solution?.id);

  // Compute tagProducts mapping from products
  const tagProducts = useMemo(() => {
    if (!products) return {};

    const mapping: Record<string, string[]> = {};
    products.forEach((product) => {
      const tags = product.tags || [];
      tags.forEach((tag: any) => {
        if (tag?.id) {
          if (!mapping[tag.id]) {
            mapping[tag.id] = [];
          }
          mapping[tag.id].push(product.id);
        }
      });
    });

    return mapping;
  }, [products]);

  const query = useMarketOrders();

  // Process the data: enrich → filter → sort
  const processedData = useMemo(() => {
    const orders = query.data?.orders;
    if (!orders || !products) return null;

    // 1. Enrich with state and key fields
    const enrichedOrders = orders.map((order) => {
      const product = products.find((p) => p.id === order.product.id);
      // Check if user has access to any variant product
      const orderVariants = (order as any).variants;
      const hasVariantAccess = Boolean(
        orderVariants?.some((v: any) =>
          products.some((p) => p.id === v.product?.id)
        )
      );
      const hasProductAccess = Boolean(product || hasVariantAccess);

      let state = 'viewonly';
      if (hasProductAccess) {
        state =
          hasRole('trader') || hasRole('manager')
            ? order.counteronly && !order.is_owner
              ? 'sfc-tradeable'
              : order.tradeable
                ? 'tradeable'
                : 'untradeable'
            : 'untradeable';
      }
      return {
        ...order,
        state,
        key: order.id,
        hasProductAccess,
      };
    });

    // 2. Filter
    const filteredOrders = filterOrders(enrichedOrders, filters, tagProducts);

    // 3. Sort
    const sorter = getSorter(sortDirection, sortColumn);
    return sorter(filteredOrders);
  }, [
    query.data?.orders,
    products,
    hasRole,
    filters,
    tagProducts,
    sortColumn,
    sortDirection,
  ]);

  return {
    ...query,
    data: processedData,
  };
};
